import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  AttachMoney as CashIcon
} from '@mui/icons-material';

const PaymentDialog = ({ open, onClose, onConfirm, invoice }) => {
  const [paymentType, setPaymentType] = useState('');
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    setPaymentType('');
    setAuthorizationCode('');
    setError('');
    onClose();
  };

  const handleConfirm = () => {
    if (!paymentType) {
      setError('Please select a payment type');
      return;
    }

    // Validate authorization code for certain payment types
    if ((paymentType === 'CREDIT' || paymentType === 'DEBIT') && !authorizationCode.trim()) {
      setError('Authorization code is required for credit/debit payments');
      return;
    }

    onConfirm({
      status: 'PAID',
      paymentType,
      authorizationCode: authorizationCode.trim() || null
    });

    handleClose();
  };

  const getPaymentIcon = (type) => {
    switch (type) {
      case 'E-TRANSFER':
        return <BankIcon />;
      case 'CASH':
        return <CashIcon />;
      case 'CREDIT':
      case 'DEBIT':
        return <CreditCardIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const isAuthorizationRequired = paymentType === 'CREDIT' || paymentType === 'DEBIT';

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'primary.main',
        color: 'white',
        py: 2
      }}>
        <PaymentIcon />
        <Typography variant="h6" component="div">
          Mark Invoice as Paid
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {invoice && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Invoice #{invoice.invoiceNumber}
            </Typography>
            <Typography variant="h6" color="primary">
              ${invoice.totalAmount?.toFixed(2) || '0.00'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customer: {invoice.customer?.name || 'N/A'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Payment Type *</InputLabel>
          <Select
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
            label="Payment Type *"
            startAdornment={getPaymentIcon(paymentType)}
          >
            <MenuItem value="E-TRANSFER">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BankIcon fontSize="small" />
                E-Transfer
              </Box>
            </MenuItem>
            <MenuItem value="CASH">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CashIcon fontSize="small" />
                Cash
              </Box>
            </MenuItem>
            <MenuItem value="CREDIT">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon fontSize="small" />
                Credit Card
              </Box>
            </MenuItem>
            <MenuItem value="DEBIT">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon fontSize="small" />
                Debit Card
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Authorization Code"
          value={authorizationCode}
          onChange={(e) => setAuthorizationCode(e.target.value)}
          placeholder={isAuthorizationRequired ? "Required for credit/debit payments" : "Optional"}
          helperText={
            isAuthorizationRequired 
              ? "Authorization code is required for credit/debit payments"
              : "Enter authorization code if available"
          }
          required={isAuthorizationRequired}
          variant="outlined"
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          color="success"
          startIcon={<PaymentIcon />}
        >
          Mark as Paid
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
